import * as Sentry from "@sentry/react";

/*
 * Recovery for failed lazy-chunk loads, which otherwise leave the user on a
 * blank screen.
 *
 * Vite dispatches `vite:preloadError` on `window` whenever a dynamically
 * imported module fails to load. This typically happens after a deploy: a page
 * loaded before the deploy asks for a chunk that the (cache-first) Service
 * Worker no longer has precached and that the network momentarily fails to
 * serve. It fires for every Vite dynamic import, including those outside
 * `React.lazy` that no React error boundary would catch.
 *
 * The recovery escalates and is throttled to avoid reload loops:
 *   1. First failure: a plain reload, enough when the network/CDN just needs to
 *      re-fetch the current version.
 *   2. A failure again within the throttle window: the reload did not help — the
 *      tab is pinned to a stale, cache-first Service Worker — so we unregister
 *      the Service Worker and wipe its caches before reloading.
 *   3. Still failing: we stop reloading and let the error surface to the React
 *      error boundary (the "Ooops" screen), avoiding an infinite reload loop.
 */

const LAST_FAILURE_AT_KEY = "chunkLoadRecovery:lastFailureAt";
const ATTEMPTS_KEY = "chunkLoadRecovery:attempts";
const THROTTLE_WINDOW_MS = 15_000;

export const isChunkLoadError = (reason: unknown): boolean => {
  if (reason === null || reason === undefined) {
    return false;
  }
  const message = reason instanceof Error ? reason.message : String(reason);
  const name = (reason as { name?: string }).name;
  return (
    name === "ChunkLoadError" ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
};

const unregisterServiceWorkersAndClearCaches = async (): Promise<void> => {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
    }
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
    }
  } catch (error) {
    // Best-effort cleanup: reload regardless (handled by the caller's `finally`).
    console.error("Failed to clear Service Worker and caches during chunk recovery", error);
  }
};

/**
 * Escape a stale, cache-first Service Worker by unregistering it and wiping its
 * caches, then reload. Exposed so the generic error screen's "Reload" button can
 * reuse it when the underlying error is a chunk load failure.
 */
export const recoverFromChunkLoadError = (): void => {
  void unregisterServiceWorkersAndClearCaches().finally(() => window.location.reload());
};

const countRecentAttempts = (now: number): number => {
  const lastFailureAt = Number(sessionStorage.getItem(LAST_FAILURE_AT_KEY)) || 0;
  const isWithinWindow = now - lastFailureAt < THROTTLE_WINDOW_MS;
  return isWithinWindow ? Number(sessionStorage.getItem(ATTEMPTS_KEY)) || 0 : 0;
};

// `window.location.reload()` is asynchronous: the page keeps running until the
// navigation actually happens, so sibling lazy imports failing in the same tick
// each dispatch their own `vite:preloadError`. Once a recovery is scheduled we
// absorb those extra events, so one user-visible failure consumes exactly one
// escalation step. In-memory on purpose: the flag must die with the page.
let isRecoveryScheduled = false;

/**
 * Runs the escalating recovery. Returns `true` when it has taken over (a reload
 * is scheduled and the caller should prevent the default error handling), or
 * `false` when recovery is exhausted and the error should surface normally.
 */
const handleChunkLoadFailure = (reason: unknown): boolean => {
  if (isRecoveryScheduled) {
    return true;
  }

  const now = Date.now();
  const attempts = countRecentAttempts(now);

  sessionStorage.setItem(LAST_FAILURE_AT_KEY, String(now));
  sessionStorage.setItem(ATTEMPTS_KEY, String(attempts + 1));

  Sentry.withScope(scope => {
    scope.setExtra("attempts", attempts);
    scope.setExtra("reason", reason instanceof Error ? reason.message : String(reason));
    Sentry.captureMessage("Lazy chunk failed to load");
  });

  if (attempts === 0) {
    isRecoveryScheduled = true;
    window.location.reload();
    return true;
  }
  if (attempts === 1) {
    isRecoveryScheduled = true;
    recoverFromChunkLoadError();
    return true;
  }
  return false;
};

export const setupChunkLoadRecovery = (): void => {
  window.addEventListener("vite:preloadError", event => {
    const handled = handleChunkLoadFailure((event as Event & { payload?: unknown }).payload);
    if (handled) {
      // Stop Vite from re-throwing the error while the page reloads.
      event.preventDefault();
    }
  });
};
